import type {
	FormJSONObject,
	FormSummaryJSONObject,
	IkeAction,
	IkeActionAnnotationSummaryCreate,
	IkeActionCalibrationGetOrCreate,
	IkeActionCaptureCreate,
	IkeActionCaptureImage,
	IkeActionCollectionAssign,
	IkeActionCollectionCSVImport,
	IkeActionCollectionCaptureMaskGet,
	IkeActionCollectionDelete,
	IkeActionCollectionDuplicate,
	IkeActionCollectionGetOrCreate,
	IkeActionCollectionMigrate,
	IkeActionCollectionMoveToDepartment,
	IkeActionCollectionMoveToJob,
	IkeActionCollectionRepair,
	IkeActionCollectionRevert,
	IkeActionCollectionSummaryRefresh,
	IkeActionDepartmentGetDepartmentId,
	IkeActionDepartmentInsightProcess,
	IkeActionDepartmentInvite,
	IkeActionDepartmentUserList,
	IkeActionDepartmentUserRole,
	IkeActionEvent,
	IkeActionExportImportableExcelTemplate,
	IkeActionFormCopy,
	IkeActionFormDelete,
	IkeActionFormRestore,
	IkeActionFormShare,
	IkeActionFormSummary,
	IkeActionPLAImport,
	IkeActionPLAImportPoleforeman,
	IkeActionPLAImportSavePoleForemanDB,
	IkeActionPLAMetadata,
	IkeActionSave,
	IkeActionSearch,
	IkeActionTagGet,
	IkeActionTagGetOrCreate,
	IkeActionTaskCreate,
	IkeActionTaskStatus,
	IkeActionUserIsLicensed,
	IkeActionUserProfile,
	Modifications,
	RefreshIPFFormLicenseCheck,
	TagJSONObject,
	TokenJSONObject,
	CollectionJSONObject,
	ParseJSONObject,
} from '@ikegps/core-api';
import type { Log } from '../log';
import { type CollectionModel, type FormModel, type IkeCollectionLoader, IkeModel } from '../model';
import { IkeRestBatchControl } from './batch';
import { IkeRestCacheControl } from './cache';
import { type IkeFetchable, IkeFetchableMany, IkeFetchableOne, IkeFetchableTransform } from './fetchable';
import type { IkeRestObject } from './objects/base';
import { IkeObjectCollectionModel } from './objects/collection.object';
import { IkeObjectFormModel } from './objects/form.object';
import { IkeRestObjects } from './objects/object';
import { IkeRestQuery } from './query';
import { IkeRest } from './rest';
import { IkeRestDepartmentAttachment } from './rest.department.attachment';
import { IkeRestDepartmentImage } from './rest.department.image';
import type { IkeRestUser } from './rest.user';

export class IkeRestDepartment implements IkeCollectionLoader {
	private batcher: IkeRestBatchControl;

	Objects: IkeRestObjects = IkeRestObjects;
	Attachment: IkeRestDepartmentAttachment = new IkeRestDepartmentAttachment(this);

	tags: IkeFetchableMany<TagJSONObject>;
	tokens: IkeFetchableMany<TokenJSONObject>;
	formSummaries: IkeFetchableMany<FormSummaryJSONObject>;
	archivedFormSummaries: IkeFetchableMany<FormSummaryJSONObject>;

	empty: IkeFetchableOne<null>;

	cache: IkeRestCacheControl;

	boundUser: IkeRestUser;
	departmentId: string;
	log: Log;

	Tag = {
		getById: (objectId: string) => this.getOne(this.Objects.Tag, 'uuid', objectId),
		getByName: this.bindAction<IkeActionTagGet>('tag.get'),
		getOrCreate: this.bindAction<IkeActionTagGetOrCreate>('tag.create'),
		Summary: {
			getById: (objectId: string) => this.getOne(this.Objects.TagSummary, 'objectId', objectId),
		},
	};

	Event = {
		create: this.bindAction<IkeActionEvent>('event.create'),
	};

	Calibration = {
		getById: (objectId: string) => this.getOne(this.Objects.Calibration, 'objectId', objectId),

		create: this.bindAction<IkeActionCalibrationGetOrCreate>('calibration.create'),
	};

	Capture = {
		getById: (objectId: string) => this.getOne(this.Objects.Capture, 'objectId', objectId),
		getByUuid: (uuid: string) => this.getOne(this.Objects.Capture, 'uuid', uuid),

		Image: new IkeRestDepartmentImage(this),
		data: this.bindAction<IkeActionCaptureImage>('capture.data'),
		create: this.bindAction<IkeActionCaptureCreate>('capture.create'),
		getMask: this.bindAction<IkeActionCollectionCaptureMaskGet>('collection.capture.mask.get'),
	};

	AnnotationSummary = {
		getByUuid: (uuid: string) => this.getOne(this.Objects.AnnotationSummary, 'uuid', uuid),
		getById: (objectId: string) => this.getOne(this.Objects.AnnotationSummary, 'collectionId', objectId),
		getByRevision: (revision: string) => this.getOne(this.Objects.AnnotationSummary, 'revision', revision),

		create: this.bindAction<IkeActionAnnotationSummaryCreate>('annotation.summary.create'),
	};

	CollectionSummary = {
		getByUuid: (uuid: string) => this.getOne(this.Objects.CollectionSummary, 'uuid', uuid),
		getById: (objectId: string) => this.getOne(this.Objects.CollectionSummary, 'collectionId', objectId),
		getByRevision: (revision: string) => this.getOne(this.Objects.CollectionSummary, 'revision', revision),

		create: this.bindAction<IkeActionCollectionSummaryRefresh>('collection.summary.create'),
	};

	Collection = {
		getById: (objectId: string) => this.getOne(this.Objects.Collection, 'objectId', objectId),
		getByUuid: (uuid: string) => this.getOne(this.Objects.Collection, 'uuid', uuid),
		getByRevision: (revision: string) => this.getOne(this.Objects.Collection, 'revision', revision),
		getModel: (revision: string) => this.getModel(revision),

		assign: this.bindAction<IkeActionCollectionAssign>('collection.assign'),
		Import: {
			csv: this.bindAction<IkeActionCollectionCSVImport>('collection.import.csv'),
		},
		exportImportableExcelTemplate: this.bindAction<IkeActionExportImportableExcelTemplate>(
			'export.importable.excel.template'
		),
		delete: this.bindAction<IkeActionCollectionDelete>('collection.delete'),
		duplicate: this.bindAction<IkeActionCollectionDuplicate>('collection.duplicate'),
		moveToJob: this.bindAction<IkeActionCollectionMoveToJob>('collection.move.to.job'),
		moveToDepartment: this.bindAction<IkeActionCollectionMoveToDepartment>('collection.move.to.department'),
		create: this.bindAction<IkeActionCollectionGetOrCreate>('collection.create'),
		search: this.bindAction<IkeActionSearch>('collection.search'),
		repair: this.bindAction<IkeActionCollectionRepair>('collection.repair'),
		revert: this.bindAction<IkeActionCollectionRevert>('collection.revert'),
		insightProcess: this.bindAction<IkeActionDepartmentInsightProcess>('department.insight.process'),
		migrate: this.bindAction<IkeActionCollectionMigrate>('collection.migrate'),
	};

	Department = {
		getById: this.bindAction<IkeActionDepartmentGetDepartmentId>('department.get.departmentid'),

		User: {
			getProfileById: (objectId: string) => this.getOne(this.Objects.UserProfile, 'objectId', objectId),
			role: this.bindAction<IkeActionDepartmentUserRole>('department.user.role'),
			list: this.bindAction<IkeActionDepartmentUserList>('department.user.list'),
			profile: this.bindAction<IkeActionUserProfile>('user.profile'),
			isLicensed: this.bindAction<IkeActionUserIsLicensed>('user.is.licensed'),
		},
		Invite: {
			do: this.bindAction<IkeActionDepartmentInvite>('department.invite'),
		},
	};

	Task = {
		create: this.bindAction<IkeActionTaskCreate>('task.create'),
		status: this.bindAction<IkeActionTaskStatus>('task.status'),
	};

	Form = {
		getById: (id: string) => this.getOne(this.Objects.Form, 'objectId', id),
		getByRevision: (revision: string) => this.getOne(this.Objects.Form, 'version', revision),
		getModel: (revision: string) => this.form('version', revision),

		delete: this.bindAction<IkeActionFormDelete>('form.delete'),
		restore: this.bindAction<IkeActionFormRestore>('form.restore'),
		share: this.bindAction<IkeActionFormShare>('form.share'),
		copy: this.bindAction<IkeActionFormCopy>('form.copy'),

		Pla: {
			getRefreshPermission: this.bindAction<RefreshIPFFormLicenseCheck>('refresh.form.ipf.license.check'),
			metadata: this.bindAction<IkeActionPLAMetadata>('form.pla.metadata'),
			import: this.bindAction<IkeActionPLAImport>('form.pla.import'),
			importPoleforeman: this.bindAction<IkeActionPLAImportPoleforeman>('form.pla.import.poleforeman'),
			savePoleForemanDb: this.bindAction<IkeActionPLAImportSavePoleForemanDB>('form.pla.import.save.poleforeman'),
		},
		Summary: {
			list: this.bindAction<IkeActionFormSummary>('form.summary'),
			getByRevision: (revision: string) => this.getOne(this.Objects.FormSummary, 'revision', revision),
		},
	};

	constructor(boundUser: IkeRestUser, departmentId: string) {
		this.boundUser = boundUser;
		this.departmentId = departmentId;
		this.log = boundUser.log.child({ departmentId });

		this.tokens = new IkeFetchableMany(this.log, IkeRest.createStore(), () => this.query(IkeRestObjects.Token).find());

		this.tags = new IkeFetchableMany(this.log, IkeRest.createStore(), () => this.query(IkeRestObjects.Tag).findAll());

		this.formSummaries = new IkeFetchableMany(this.log, IkeRest.createStore(), async () => {
			const response = await this.action<IkeActionFormSummary>('form.summary');
			return response.results;
		});

		this.archivedFormSummaries = new IkeFetchableMany(this.log, IkeRest.createStore(), async () => {
			const response = await this.action<IkeActionFormSummary>('form.summary', {
				includeArchived: true,
			});
			return response.results.filter((f) => f.isDeleted === true);
		});

		this.empty = new IkeFetchableOne(this.log, IkeRest.createStore<null>(), () => Promise.resolve(null));

		this.cache = new IkeRestCacheControl();
		this.batcher = new IkeRestBatchControl(this);
	}

	form<K extends keyof FormJSONObject & keyof FormModel>(key: K, value: string): IkeFetchable<FormModel> {
		let existing = this.cache.get(IkeObjectFormModel, key).get(key, value);
		if (existing == null) {
			existing = new IkeFetchableTransform<FormJSONObject, FormModel>(
				this.log,
				IkeRest.createStore(),
				this.getOne(IkeRestObjects.Form, key, value),
				async (formJSON: FormJSONObject): Promise<FormModel> => {
					if (formJSON == null) {
						throw new Error(`Unable to fetch form model with ${String(key)}: ${value}`);
					}
					return IkeModel.Form.read(formJSON);
				}
			);
			this.cache.get(IkeObjectFormModel, key).set(key, value, existing);
		}
		return existing;
	}

	private getModel(revision: string): IkeFetchable<CollectionModel> {
		const existing = this.cache.get(IkeObjectCollectionModel, 'revision').get('revision', revision);
		if (existing != null) {
			return existing;
		}
		const fetchable = new IkeFetchableTransform<CollectionJSONObject, CollectionModel>(
			this.log,
			IkeRest.createStore(),
			this.getOne(IkeRestObjects.Collection, 'revision', revision),
			async (c: CollectionJSONObject) => {
				if (c == null) {
					let cs = await this.CollectionSummary.getByRevision(revision).fetch();
					if (cs == null) {
						throw new Error(`Unable to fetch collection model with revision: ${revision}`);
					}
					const repaired = await this.CollectionSummary.create({ uuid: cs.uuid, repair: true });
					if (repaired) {
						c = await this.getOne(IkeRestObjects.Collection, 'uuid', cs.uuid).fetch();
						if (c == null) {
							throw new Error(`Unable to fetch collection model with uuid: ${cs.uuid}`);
						}
					} else {
						throw new Error(`Unable to repair collection model with revision: ${revision}`);
					}
				}

				const formModel = await this.form('version', c.formversion).fetch();
				const model = IkeModel.Collection.read(c, formModel, this, this.log);
				// Revision may change if collection was repaired
				this.cache.get(IkeObjectCollectionModel, 'revision').set('revision', model.revision, fetchable);

				// TODO if we ever want a model to reference another collection model
				// We cannot wait for it to be ready here.
				await model.ready();
				return model;
			}
		);

		this.cache.get(IkeObjectCollectionModel, 'revision').set('revision', revision, fetchable);
		return fetchable;
	}

	action<T extends IkeAction>(path: T['action'], data?: T['request']): Promise<T['response']> {
		return this.boundUser.core._post(path, data, this.boundUser.accessToken, this.departmentId);
	}

	actionRaw<T extends IkeAction>(path: T['action'], data?: T['request']): Promise<Response> {
		return this.boundUser.core._raw(path, data, this.boundUser.accessToken, this.departmentId);
	}

	query<T extends ParseJSONObject>(obj: IkeRestObject<T>): IkeRestQuery<T> {
		return new IkeRestQuery(this.boundUser.core, obj, this.boundUser.accessToken, this.departmentId, this.log);
	}

	async save<T extends ParseJSONObject>(
		obj: IkeRestObject<T>,
		value: T,
		modifications?: Modifications,
		silent?: boolean
	): Promise<T> {
		const response = await this.action<IkeActionSave<T>>(`${obj.actionName}.save` as any, {
			value,
			...(modifications && { modifications }),
			silent,
		});
		return response.results;
	}

	/**
	 * Get data from the API, generally should be called via the helper functions
	 *  @see IkeDepartmentRest.Collection.getByRevision
	 */
	getOne<T extends ParseJSONObject, K extends keyof T>(obj: IkeRestObject<T>, key: K, value: string): IkeFetchable<T> {
		let existing = this.cache.get(obj, key).get(key, value);
		if (existing == null) {
			const requestObj = async () => {
				const result = await this.batcher.batch(obj, key, value as any);
				if (obj.updateCache != null) {
					obj.updateCache(key, result, this);
				}
				return result;
			};

			existing = new IkeFetchableOne(this.log, IkeRest.createStore<any>(), requestObj);
			this.cache.get(obj, key).set(key, value, existing);
		}
		return existing;
	}

	/** Create a helper function to run a action */
	private bindAction<T extends IkeAction>(path: T['action']): (data: T['request']) => Promise<T['response']> {
		return (data: T['request']) => this.boundUser.core._post(path, data, this.boundUser.accessToken, this.departmentId);
	}

	updateBoundUser(newBoundUser: IkeRestUser) {
		this.boundUser = newBoundUser;
	}
}
